"use client";

import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";

/**
 * `<main>` wrapper that applies safe-area padding based on the page.
 *
 * Bottom padding (pb-safe-nav) rule:
 *   Only `/profile/*` needs main 의 pb-safe-nav.
 *   - Footer 가 모바일에 보이는 페이지(`/`, `/help/*`, `/promotions/*`, etc.)는
 *     Footer 의 자체 pb (6rem + safe) 가 이미 BottomNavBar (3.5rem + safe) 분량보다
 *     크므로 BottomNavBar 가 컨텐츠를 가리지 않는다. main 에 또 pb-safe-nav 를 주면
 *     이중 padding.
 *   - Footer 가 모바일에 hidden 인 `/s/*` 는 자체 `bottom-safe-nav` fixed 컨테이너로
 *     처리. `/messages` 목록은 자체 `pb-safe-nav` 적용.
 *   - Footer 가 null + BottomNavBar 도 없는 `/login`, `/signup`, `/auth/*`,
 *     `/account-settings/*`, `/reservations/[id]`, `/messages/[id]` 류는 pb 가 아예
 *     필요 없음 — 줘봤자 화면 하단에 빈 공간만 생긴다.
 *   - 결론: profile 만 Footer hidden + BottomNavBar 표시 + 자체 처리 없음 → main pb 필요.
 *
 * Top padding (pt-page-safe) rule:
 *   Generic page wrappers use the page-level safe-area utility. On Capacitor
 *   iOS it becomes 0 because Navbar/sticky headers own the real status-bar
 *   clearance; on browser/PWA it still follows the viewport safe-area.
 */
export default function MainContent({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const locale = useLocale();

  const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";

  const isReservationDetailPage = /^\/reservations\/[^/]+/.test(
    pathWithoutLocale,
  );
  const isAboutPage = pathWithoutLocale === "/about";
  const isSearchPage = /^\/s\//.test(pathWithoutLocale);
  const isCheckoutPage = pathWithoutLocale === "/checkout";
  const isCartPage = pathWithoutLocale === "/cart";
  const isMessagesPage = pathWithoutLocale.startsWith("/messages");
  const isProfilePage =
    (pathWithoutLocale === "/profile" ||
      pathWithoutLocale.startsWith("/profile/")) &&
    !pathWithoutLocale.startsWith("/account-settings");
  const isHomePage = pathWithoutLocale === "/";
  // 크루 트리는 자체 헤더(sticky top-0 → globals.css 가 pt-safe 자동 부여)를 쓴다.
  // 여기서 pt-page-safe 를 또 주면 헤더 위에 빈 띠가 생긴다.
  const isCrewPage =
    pathWithoutLocale === "/crew" || pathWithoutLocale.startsWith("/crew/");
  // 계정 설정은 전역 Navbar 가 없고 자체 nav(pt-safe)가 화면 최상단이다. 여기서
  // pt-page-safe 를 주면 브라우저/PWA 에서 nav 위에 inset 이 한 번 더 쌓인다.
  const isAccountSettingsPage = pathWithoutLocale.startsWith("/account-settings");

  // 상단 padding 자체 처리 페이지 (sticky header / 풀스크린 컨테이너 등)
  const noTopPadding =
    isHomePage ||
    isReservationDetailPage ||
    isAboutPage ||
    isSearchPage ||
    isCheckoutPage ||
    isCartPage ||
    isMessagesPage ||
    isProfilePage ||
    isCrewPage ||
    isAccountSettingsPage;

  // profile 만 BottomNavBar 가림 방어가 자체로도 안 되고 Footer 도 모바일에 없다.
  const needsBottomPadding = isProfilePage;

  return (
    <main
      className={cn(
        !noTopPadding && "pt-page-safe md:pt-0",
        needsBottomPadding && "pb-safe-nav",
      )}
    >
      {children}
    </main>
  );
}
